import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface ErrorInfo {
  message: string;
  stack?: string;
  name?: string;
  fileName?: string;
  lineNumber?: number;
  columnNumber?: number;
  timestamp: Date;
  componentStack?: string;
}

interface DebugContextType {
  debugMode: boolean;
  setDebugMode: (mode: boolean) => void;
  errors: ErrorInfo[];
  addError: (error: ErrorInfo) => void;
  clearErrors: () => void;
  isDebugEnabled: boolean;
  logApplicationError: (message: string, details?: any) => void;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

const STORAGE_KEY = 'eis-debug-mode';

export const DebugProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [debugMode, setDebugModeState] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorInfo[]>([]);

  // Load debug mode from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      setDebugModeState(stored === 'true');
    } else {
      // Default to env var if no stored value
      const envDebug = (import.meta as any).env.VITE_DEBUG_MODE === 'true';
      setDebugModeState(envDebug);
    }
  }, []);

  // Save debug mode to localStorage when it changes
  const setDebugMode = (mode: boolean) => {
    setDebugModeState(mode);
    localStorage.setItem(STORAGE_KEY, mode.toString());
  };

  const addError = (error: ErrorInfo) => {
    setErrors(prev => [error, ...prev].slice(0, 50)); // Keep last 50 errors
  };

  const clearErrors = () => {
    setErrors([]);
  };

  const logApplicationError = (message: string, details?: any) => {
    if (debugMode) {
      const errorInfo: ErrorInfo = {
        message,
        name: 'ApplicationError',
        timestamp: new Date(),
        ...details,
      };
      addError(errorInfo);
    }
  };

  // Check if debug is enabled (either from env or localStorage)
  const isDebugEnabled = debugMode || (import.meta as any).env.VITE_DEBUG_MODE === 'true';

  // Global error handler
  useEffect(() => {
    if (!isDebugEnabled) return;

    const handleError = (event: ErrorEvent) => {
      const errorInfo: ErrorInfo = {
        message: event.message,
        stack: event.error?.stack,
        name: event.error?.name,
        fileName: event.filename,
        lineNumber: event.lineno,
        columnNumber: event.colno,
        timestamp: new Date(),
      };
      addError(errorInfo);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorInfo: ErrorInfo = {
        message: error?.message || 'Unhandled Promise Rejection',
        stack: error?.stack,
        name: error?.name || 'UnhandledRejection',
        timestamp: new Date(),
      };
      addError(errorInfo);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [isDebugEnabled]);

  // Suppress console errors when debug mode is off
  useEffect(() => {
    if (isDebugEnabled) return;

    const originalConsoleError = console.error;
    console.error = () => {
      // Still allow Sentry to work, but suppress console output
      // You can add specific filtering here if needed
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, [isDebugEnabled]);

  return (
    <DebugContext.Provider value={{
      debugMode,
      setDebugMode,
      errors,
      addError,
      clearErrors,
      isDebugEnabled,
      logApplicationError
    }}>
      {children}
    </DebugContext.Provider>
  );
};

export const useDebug = () => {
  const context = useContext(DebugContext);
  if (context === undefined) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};